import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Beaker,
  ArrowLeft,
  ChevronRight,
  Lock,
  CheckCircle,
  Sparkles,
  Loader2,
  Crown,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const LabsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, fetchUser } = useAuth();
  const [labs, setLabs] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedLockedLab, setSelectedLockedLab] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  const effectiveTier = user?.effective_tier || user?.tier || 'free';

  useEffect(() => {
    loadLabs();
  }, []);

  const loadLabs = async () => {
    try {
      const response = await api.getLabs();
      setLabs(response.data.labs);
      setRecommended(response.data.recommended || []);
    } catch (error) {
      toast.error('Could not load labs');
    } finally {
      setLoading(false);
    }
  };

  const handleLockedLabClick = (lab) => {
    setSelectedLockedLab(lab);
    setShowUpgradeModal(true);
  };

  const handleUpgrade = async (tier = 'basic') => {
    setUpgrading(true);
    try {
      const lookupKey = tier === 'pro' ? 'pro_monthly' : 'basic_monthly';
      const response = await api.createCheckout(lookupKey, window.location.origin);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
        setUpgrading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setUpgrading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-mint text-ocean-blue';
      case 'intermediate': return 'bg-sky-blue text-white';
      case 'advanced': return 'bg-coral text-white';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getTierBadge = (tier) => {
    if (tier === 'basic') return { label: 'Basic', className: 'bg-sky-blue' };
    if (tier === 'pro') return { label: 'Pro', className: 'bg-coral' };
    return { label: tier, className: '' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-mint">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-ocean-blue hover:bg-white/20"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/30 flex items-center justify-center">
                  <Beaker className="w-6 h-6 text-ocean-blue" />
                </div>
                <div>
                  <h1 className="font-bold text-ocean-blue">{t('nav.labs')}</h1>
                  <p className="text-xs text-ocean-blue/70">{t('labs.subtitle')}</p>
                </div>
              </div>
            </div>
            <Badge variant="outline" className={`capitalize ${
              effectiveTier === 'pro' ? 'border-coral text-coral' :
              effectiveTier === 'basic' ? 'border-ocean-blue text-ocean-blue' :
              'border-muted-foreground text-muted-foreground'
            }`}>
              {effectiveTier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
              {effectiveTier} tier
            </Badge>
          </div>
        </div>
        <div className="wave-divider h-8" />
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t('labs.title')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('labs.subtitle')}
          </p>
        </motion.div>

        {/* Recommended Labs */}
        {recommended.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-coral" />
              <h2 className="text-lg font-bold text-foreground">{t('labs.recommendedForYou')}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {recommended.map((lab) => (
                <Card 
                  key={lab.id} 
                  className="rounded-xl border-2 border-coral/30 hover:border-coral transition-colors cursor-pointer"
                  onClick={() => navigate(`/lab/${lab.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-coral/20 flex items-center justify-center">
                        <Beaker className="w-6 h-6 text-coral" />
                      </div>
                      <div className="flex-1">
                        <Badge className="mb-2 bg-coral/10 text-coral border-0 text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          Recommended
                        </Badge>
                        <h3 className="font-semibold text-foreground">{lab.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {lab.recommendation_reason}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        )}

        {/* All Labs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-xl font-bold text-foreground mb-6">{t('labs.allLabs')}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {labs.map((lab, index) => {
              const tierBadge = getTierBadge(lab.tier);
              
              return (
                <motion.div
                  key={lab.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`h-full rounded-2xl border-0 shadow-lg overflow-hidden ${
                      lab.locked ? 'opacity-80' : 'hover:shadow-xl transition-shadow cursor-pointer'
                    }`}
                    onClick={() => !lab.locked && navigate(`/lab/${lab.id}`)}
                    data-testid={`lab-card-${lab.id}`}
                  >
                    <div className={`h-2 ${lab.tier === 'pro' ? 'bg-coral' : 'bg-mint'}`} />
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className={`w-12 h-12 rounded-xl ${lab.locked ? 'bg-muted' : lab.tier === 'pro' ? 'bg-coral' : 'bg-mint'} flex items-center justify-center`}>
                          {lab.locked ? (
                            <Lock className="w-6 h-6 text-muted-foreground" />
                          ) : lab.completed ? (
                            <CheckCircle className="w-6 h-6 text-white" />
                          ) : (
                            <Beaker className="w-6 h-6 text-white" />
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Badge className={getDifficultyColor(lab.difficulty)}>
                            {lab.difficulty}
                          </Badge>
                          <Badge className={tierBadge.className}>
                            {lab.tier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
                            {tierBadge.label}
                          </Badge>
                        </div>
                      </div>
                      <CardTitle className="mt-4">{lab.title}</CardTitle>
                      <CardDescription>{lab.description}</CardDescription>
                      {lab.emotional_tone && (
                        <p className="text-xs text-ocean-blue/80 italic mt-2">
                          &ldquo;{lab.emotional_tone}&rdquo;
                        </p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {lab.step_count} {t('labs.steps')}
                        </span>
                        {lab.locked ? (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLockedLabClick(lab);
                            }}
                            className="rounded-full"
                            data-testid={`unlock-${lab.id}`}
                          >
                            <Lock className="w-4 h-4 mr-2" />
                            {t('labs.unlock')}
                          </Button>
                        ) : (
                          <Button 
                            size="sm"
                            className={`rounded-full ${lab.tier === 'pro' ? 'bg-coral hover:bg-coral/90' : 'gradient-ocean'} text-white`}
                            data-testid={`start-${lab.id}`}
                          >
                            {lab.completed ? t('labs.review') : t('common.start')}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Free Tier CTA */}
        {effectiveTier === 'free' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-16"
          >
            <Card className="p-8 rounded-3xl bg-gradient-to-br from-mint via-mint/90 to-mint/80 border-0 text-center">
              <Beaker className="w-12 h-12 mx-auto mb-4 text-ocean-blue" />
              <h2 className="text-2xl font-bold text-ocean-blue mb-2">
                {t('labs.unlockAllLabs')}
              </h2>
              <p className="text-ocean-blue/80 mb-6 max-w-lg mx-auto">
                {t('labs.unlockDescription')}
              </p>
              <Button 
                size="lg"
                onClick={() => setShowUpgradeModal(true)}
                className="rounded-full gradient-ocean text-white px-8"
                data-testid="upgrade-labs-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {t('labs.viewPlans')}
              </Button>
            </Card>
          </motion.div>
        )}
      </main>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-ocean-blue" />
              {selectedLockedLab ? `${t('labs.unlock')} ${selectedLockedLab.title}` : t('labs.upgradeYourPlan')}
            </DialogTitle>
            <DialogDescription>
              {selectedLockedLab 
                ? t('labs.labRequires', { tier: selectedLockedLab.tier === 'pro' ? 'Pro' : 'Basic' })
                : t('labs.choosePlan')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Basic Plan */}
            <div className={`p-4 rounded-xl border-2 ${effectiveTier === 'basic' ? 'border-ocean-blue bg-ocean-blue/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">Basic</h3>
                <Badge className="bg-ocean-blue">$29/mo</Badge>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>✓ Idea Lab & Pitch Lab</li>
                <li>✓ All Layer 0 & 1 modules</li>
                <li>✓ Regional Rooms access</li>
              </ul>
              <Button 
                className="w-full gradient-ocean" 
                onClick={() => handleUpgrade('basic')}
                disabled={upgrading || effectiveTier === 'basic' || effectiveTier === 'pro'}
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {effectiveTier === 'basic' || effectiveTier === 'pro' ? t('labs.unlocked') : t('labs.upgradeToBasic')}
              </Button>
            </div>
            
            {/* Pro Plan */}
            <div className={`p-4 rounded-xl border-2 ${effectiveTier === 'pro' ? 'border-coral bg-coral/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-coral" />
                  Pro
                </h3>
                <Badge className="bg-coral">$99/mo</Badge>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>✓ All labs including Customer Discovery & Investor Readiness</li>
                <li>✓ Advanced modules</li>
                <li>✓ Certification system</li>
              </ul>
              <Button 
                className="w-full bg-coral hover:bg-coral/90" 
                onClick={() => handleUpgrade('pro')}
                disabled={upgrading || effectiveTier === 'pro'}
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {effectiveTier === 'pro' ? t('labs.currentPlan') : t('labs.upgradeToPro')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LabsPage;
